import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import TopNavigation from './TopNavigation';
import SideNavigation from './SideNavigation';
import propertyService from '../services/propertyService';
import { Property } from '../models/property';
import { useAuthToken } from './AuthTokenProvider';

const MainLayout: React.FC = () => {
  const [sideNavOpen, setSideNavOpen] = useState<boolean>(window.innerWidth >= 768); // Open by default on larger screens
  const [currentProperty, setCurrentProperty] = useState<Property | null>(null);
  const [propertyLoaded, setPropertyLoaded] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const { tokenSet, isLoading: authLoading } = useAuthToken();
  
  // Extract property ID from URL path directly - useParams won't work at this level
  const propertyMatchWithStar = useMatch('/property/:propertyId/*');
  const propertyMatchSimple = useMatch('/property/:propertyId');
  
  // Use the match that worked - prefer the one with * for nested routes
  const propertyMatch = propertyMatchWithStar || propertyMatchSimple;
  const propertyId = propertyMatch?.params?.propertyId;

  // Fetch current property data if we're on a property page
  useEffect(() => {
    const fetchPropertyDetails = async () => {
      if (propertyId && isAuthenticated && tokenSet) {
        try {
          const property = await propertyService.getProperty(propertyId);
          setCurrentProperty(property);
          setPropertyLoaded(true);
        } catch (error) {
          console.error('Error fetching property details:', error);
          setCurrentProperty(null);
          setPropertyLoaded(true); // Mark as loaded even though it failed
        }
      } else {
        setCurrentProperty(null);
        setPropertyLoaded(true); // Mark as loaded even if we don't need property data
      }
    };

    if (propertyId && !authLoading && tokenSet) {
      fetchPropertyDetails();
    } else {
      setCurrentProperty(null);
      setPropertyLoaded(true); // Mark as loaded if we're not going to fetch
    }
  }, [propertyId, isAuthenticated, tokenSet, authLoading]);

  const sideNavToggle = () => {
    setSideNavOpen(!sideNavOpen);
  };

  const newIssue = () => {
    navigate("/property");
  };

  // Determine if we should render the sidebar
  const shouldRenderSidebar = () => {
    // If we're on a property page with a propertyId, wait for data to load
    if (location.pathname.includes('/property/') && propertyId) {
      return propertyLoaded;
    }
    // Otherwise always render the sidebar
    return true;
  };

  const renderSidebar = shouldRenderSidebar();

  return (
    <>
      {/* Top Navigation */}
      <TopNavigation sideNavToggle={sideNavToggle} newIssue={newIssue} />
      
      {/* Side Navigation */}
      {renderSidebar && (
        <SideNavigation 
          isOpen={sideNavOpen} 
          toggleDrawer={sideNavToggle} 
          currentProperty={currentProperty}
        />
      )}
    </>
  );
};

export default MainLayout;
